import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { http } from '../client.ts';
import type { Travel } from '../schemas/travel.ts';

/**
 * Travel hooks. The list-by-visitor endpoint is read frequently from the
 * Home itinerary editor; saves invalidate it. Sorted variant is used by
 * the legacy "report" screens.
 */

export function useTravelsByVisitor(visitorId: number | null | undefined) {
  return useQuery({
    queryKey: ['travels', 'byVisitor', visitorId],
    queryFn: () => http.get<Travel[]>(`/api/travelsByVisitor?visitorId=${visitorId}`),
    enabled: visitorId != null,
  });
}

export function useTravelsByGroup(groupId: number | null | undefined) {
  return useQuery({
    queryKey: ['travels', 'byGroup', groupId],
    queryFn: () => http.get<Travel[]>(`/api/travelsByGroup?groupId=${groupId}`),
    enabled: groupId != null,
  });
}

export function useSaveTravel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (t: Travel) => {
      if (t.id == null) return http.post<Travel>('/api/travels', t);
      return http.put<Travel>('/api/travels', t);
    },
    onSuccess: saved => {
      qc.invalidateQueries({ queryKey: ['travels'] });
      if (saved.visitorId != null) qc.invalidateQueries({ queryKey: ['travels', 'byVisitor', saved.visitorId] });
      if (saved.groupId != null) qc.invalidateQueries({ queryKey: ['travels', 'byGroup', saved.groupId] });
    },
  });
}

export function useDeleteTravel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => http.delete(`/api/travels/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['travels'] }),
  });
}
